/**
 * Module dependencies.
 */

import { Button, Subtitle, Type } from 'src/components/core';
import { NextPage } from 'next/types';
import { awards } from 'src/core/content-config/awards';
import { color, media, units } from '@untile/react-components';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import AwardCard from 'src/components/cards/award-card';
import Container from 'src/components/core/layout/container';
import Metatags from 'src/components/core/metatags';
import map from 'lodash/map';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: ${color('backgrounds.taupe01')};
  padding: ${units(14)} 0 ${units(8)};
  position: relative;

  ${media.min('md')`
    padding: ${units(20)} 0 ${units(18)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. title    .'
    '. subtitle .'
    '. button   .'
    '. awards   .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${media.min('md')`
    grid-template-areas:
      '. title    .      .'
      '. subtitle button .'
      '. awards   awards .';
    grid-template-columns: 1fr 4fr 6fr 1fr;
    grid-template-rows: repeat(3, max-content);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;

  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Subtitle)`
  grid-area: subtitle;

  ${media.max('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  grid-area: button;

  ${media.min('md')`
    align-self: flex-end;
    display: flex;
    justify-self: flex-end;
  `}
`;

/**
 * `AwardsCardsWrapper` styled component.
 */

const AwardsCardsWrapper = styled.div`
  display: grid;
  grid-area: awards;
  grid-gap: ${units(3)} ${units(2.5)};
  grid-template-columns: repeat(2, 1fr);
  padding-top: ${units(6)};

  ${media.min('ms')`
    grid-gap: ${units(9)} 25px;
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.min('md')`
    padding-top: ${units(9)};
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(4, 1fr);
 `}
`;

/**
 * `Awards` page.
 */

const Awards: NextPage = () => {
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <>
      <Metatags
        description={translate('awards:subtitle')}
        pageTitle={translate('awards:title')}
        title={translate('awards:title')}
      />

      <Wrapper>
        <StyledContainer>
          <Grid>
            <Title {...animProps}>{translate('awards:title')}</Title>

            <Description {...animProps}>
              {translate('awards:subtitle')}
            </Description>

            <ButtonWrapper {...animProps}>
              <Button
                colorTheme={'secondary'}
                href={routeResolver('contactUs')}
                size={'large'}
              >
                {translate('common:actions.reachOutToUs')}
              </Button>
            </ButtonWrapper>

            <AwardsCardsWrapper>
              {map(awards, ({ date, imageUrl, title }, index) => (
                <div
                  data-scroll={'fade-in-up-small'}
                  data-scroll-animation-delay={'150'}
                  data-scroll-offset={'20%'}
                  key={index}
                >
                  <AwardCard
                    imageUrl={imageUrl}
                    label={date}
                    title={title}
                  />
                </div>
              ))}
            </AwardsCardsWrapper>
          </Grid>
        </StyledContainer>
      </Wrapper>
    </>
  );
};

/**
 * Export `Awards` page.
 */

export default Awards;
